import { useState } from 'react';
import { Plus, Save, X, Pencil, Trash2, Check } from 'lucide-react';
import { cashCockpitApi } from '../../services/cashCockpitApi';
import type { CockpitBankAccount, CounterpartyGroup } from '../../types/cashCockpit';
import IntercompanyMappingPanel from './IntercompanyMappingPanel';

interface Props {
    groups: CounterpartyGroup[];
    bankAccounts: CockpitBankAccount[];
    canEdit: boolean;
    onChanged: () => void;
}

/**
 * Admin-only panel to define CounterpartyGroup rollups across company borders.
 * Example: "UBS Total" can map bin-UBS, dbi-UBS, dch-UBS into one risk line.
 */
export default function SettingsTab({ groups, bankAccounts, canEdit, onChanged }: Props) {
    const [draft, setDraft] = useState<{ groupName: string; description: string; memberBankIds: string[] } | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editBuf, setEditBuf] = useState<{ groupName: string; description: string; memberBankIds: string[] } | null>(null);
    const [error, setError] = useState<string | null>(null);

    const onSaveNew = async () => {
        if (!draft) return;
        if (!draft.groupName.trim()) { setError('Group name is required'); return; }
        try {
            await cashCockpitApi.createGroup({
                groupName: draft.groupName.trim(),
                description: draft.description || undefined,
                memberBankIds: draft.memberBankIds,
            });
            setDraft(null);
            setError(null);
            onChanged();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Save failed');
        }
    };

    const onStartEdit = (g: CounterpartyGroup) => {
        setEditingId(g.id);
        setEditBuf({
            groupName: g.groupName,
            description: g.description ?? '',
            memberBankIds: [...g.memberBankIds],
        });
    };

    const onSaveEdit = async () => {
        if (editingId == null || !editBuf) return;
        try {
            await cashCockpitApi.updateGroup(editingId, {
                groupName: editBuf.groupName,
                description: editBuf.description,
                memberBankIds: editBuf.memberBankIds,
            });
            setEditingId(null);
            setEditBuf(null);
            setError(null);
            onChanged();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Update failed');
        }
    };

    const onDelete = async (id: number) => {
        try {
            await cashCockpitApi.deleteGroup(id);
            onChanged();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Delete failed');
        }
    };

    const toggleMember = (targetList: string[], bankId: string, setList: (v: string[]) => void) => {
        if (targetList.includes(bankId)) setList(targetList.filter(b => b !== bankId));
        else setList([...targetList, bankId]);
    };

    return (
        <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-900">Counterparty Groups</h3>
                    {canEdit && !draft && (
                        <button
                            onClick={() => setDraft({ groupName: '', description: '', memberBankIds: [] })}
                            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            <Plus className="w-4 h-4" /> New Group
                        </button>
                    )}
                </div>
                <p className="text-xs text-gray-500 mb-3">
                    Define custom rollups (e.g. "UBS Total" combining all UBS accounts across entities).
                    Unmapped accounts fall back to their individual bank name in the Counterparty tab.
                </p>

                {error && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3">{error}</div>
                )}

                {draft && (
                    <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 mb-3">
                        <div className="flex items-center gap-2 mb-2">
                            <input type="text" placeholder="Group name (e.g. UBS Total)" value={draft.groupName}
                                onChange={e => setDraft({ ...draft, groupName: e.target.value })}
                                className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
                            />
                            <input type="text" placeholder="Description (optional)" value={draft.description}
                                onChange={e => setDraft({ ...draft, description: e.target.value })}
                                className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
                            />
                            <button onClick={onSaveNew} className="text-green-600 hover:text-green-800"><Save className="w-4 h-4" /></button>
                            <button onClick={() => setDraft(null)} className="text-gray-500 hover:text-gray-700"><X className="w-4 h-4" /></button>
                        </div>
                        <MemberPicker
                            bankAccounts={bankAccounts}
                            selectedBankIds={draft.memberBankIds}
                            onToggle={(id) => toggleMember(draft.memberBankIds, id, (v) => setDraft({ ...draft, memberBankIds: v }))}
                        />
                    </div>
                )}

                <div className="space-y-3">
                    {groups.length === 0 && !draft && (
                        <div className="text-center text-sm text-gray-500 py-6">
                            No groups configured. Every bank will show as its own counterparty.
                        </div>
                    )}
                    {groups.map(g => {
                        const isEditing = editingId === g.id;
                        const buf = isEditing ? editBuf : null;
                        return (
                            <div key={g.id} className="border border-gray-200 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    {isEditing && buf ? (
                                        <>
                                            <input type="text" value={buf.groupName}
                                                onChange={e => setEditBuf({ ...buf, groupName: e.target.value })}
                                                className="border border-gray-300 rounded px-2 py-1 text-sm font-semibold"
                                            />
                                            <input type="text" value={buf.description}
                                                onChange={e => setEditBuf({ ...buf, description: e.target.value })}
                                                className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
                                            />
                                            <button onClick={onSaveEdit} className="text-green-600 hover:text-green-800"><Save className="w-4 h-4" /></button>
                                            <button onClick={() => { setEditingId(null); setEditBuf(null); }} className="text-gray-500"><X className="w-4 h-4" /></button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="font-semibold">{g.groupName}</div>
                                            <div className="text-xs text-gray-500 flex-1">{g.description}</div>
                                            <span className="text-xs text-gray-500">{g.memberCount} accounts</span>
                                            {canEdit && (
                                                <>
                                                    <button onClick={() => onStartEdit(g)} className="text-blue-600 hover:text-blue-800"><Pencil className="w-4 h-4" /></button>
                                                    <button onClick={() => onDelete(g.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                                {isEditing && buf ? (
                                    <MemberPicker
                                        bankAccounts={bankAccounts}
                                        selectedBankIds={buf.memberBankIds}
                                        onToggle={(id) => toggleMember(buf.memberBankIds, id, (v) => setEditBuf({ ...buf, memberBankIds: v }))}
                                    />
                                ) : (
                                    <div className="flex flex-wrap gap-1">
                                        {g.memberBankIds.map(id => (
                                            <span key={id} className="text-[10px] bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 font-mono">
                                                {id}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <IntercompanyMappingPanel canEdit={canEdit} />
        </div>
    );
}

function MemberPicker({
    bankAccounts, selectedBankIds, onToggle,
}: { bankAccounts: CockpitBankAccount[]; selectedBankIds: string[]; onToggle: (id: string) => void }) {
    const grouped = bankAccounts.reduce((acc, b) => {
        if (!acc[b.companyCode]) acc[b.companyCode] = [];
        acc[b.companyCode].push(b);
        return acc;
    }, {} as Record<string, CockpitBankAccount[]>);
    return (
        <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mt-2 max-h-64 overflow-y-auto border border-gray-200 bg-white p-2 rounded">
            {Object.entries(grouped).sort().map(([company, accounts]) => (
                <div key={company} className="text-xs">
                    <div className="font-semibold uppercase text-gray-700 mb-1">{company}</div>
                    {accounts.map(a => {
                        const selected = selectedBankIds.includes(a.bankID);
                        return (
                            <label key={a.bankID} className={`flex items-center gap-1 py-0.5 cursor-pointer ${selected ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
                                <input type="checkbox" checked={selected} onChange={() => onToggle(a.bankID)} />
                                <span className="truncate">{a.bankAccountName}</span>
                                {selected && <Check className="w-3 h-3 flex-shrink-0" />}
                            </label>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}
